import { StyleSheet, View } from 'react-native';
import { EsSignupContainer } from '../../components/ScreenContainer/EsSignupContainer';
import { SignUpdata } from '../../components/EsSignupForm/EsSignupForm';
import { useDispatchPromise } from '../../utils/AppUtils';
import { getHomeRouteForView, normalizeDashboardView } from '../../utils/dashboardView';
import { getHostDashboard } from '../../redux/buzzbridge/actions';
import {
    IFieldAvailabilityData,
    TUserField,
    createUserProfile,
    getPhoneVerificationCode,
    startUserDetailsAvailabilitySearch,
    updateUserProfile,
} from '../../redux/user/actions';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../types/global/routes.types';
import { useState } from 'react';
import { TOnChangePayload } from '../../components/EsSignupForm/Interests';
import { SentryCapture } from '../../analytics/Sentry';

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});

type BuzzApplySignupState = {
    applicationEmail?: string;
    applicationPassword?: string;
    campaignApply?: string;
};

const Signup = () => {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const navigate = useNavigate();
    const location = useLocation();
    const buzzApply = (location.state as BuzzApplySignupState | null) ?? null;
    const initialFormData =
        buzzApply?.applicationEmail != null && buzzApply.applicationEmail !== ''
            ? {
                  email: buzzApply.applicationEmail,
                  ...(buzzApply.applicationPassword ? { password: buzzApply.applicationPassword } : {}),
              }
            : undefined;
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [resendInProgress, setResendInProgress] = useState(false);

    const onSubmitSignUpOTP = (data: SignUpdata, code?: string[], next?: VoidFunction) => {
        const payload = {
            ...data,
            code: code?.join(''),
        };
        setOtpLoading(true);
        dispatchPromise(createUserProfile(payload))
            .then(() => {
                setOtpLoading(false);
                next && next();
                setLoading(false);
            })
            .catch(e => {
                setOtpLoading(false);
                setLoading(false);
            });
    };

    const checkUserDataAvailability = (data: SignUpdata, next: () => void, field: TUserField) => {
        const payload: IFieldAvailabilityData =
            field === 'userName'
                ? { field: 'userName', request: { userName: data.userName } }
                : {
                      field: 'phoneNumber',
                      request: { phoneNumber: data.phoneNumber, email: data.email, twofa_verified: true },
                  };

        setLoading(true);
        dispatchPromise(startUserDetailsAvailabilitySearch(payload))
            .then(() => {
                setLoading(false);
                if (field !== 'userName') next();
            })
            .catch(e => {
                setLoading(false);
            });
    };

    const getSignUpOTP = (data: SignUpdata, next: () => void, resend?: boolean): void | Promise<unknown> => {
        const payload = { ...data, resendToken: resend };
        if (resend) {
            setResendInProgress(true);
            return dispatchPromise(getPhoneVerificationCode(payload))
                .then((response: any) => {
                    setResendInProgress(false);
                    setLoading(false);
                    return response;
                })
                .catch(e => {
                    setResendInProgress(false);
                    setLoading(false);
                    throw e;
                });
        } else {
            setLoading(!loading);
            return dispatchPromise(getPhoneVerificationCode(payload))
                .then((response: any) => {
                    setLoading(!loading);
                    next();
                    return response;
                })
                .catch(e => {
                    setLoading(!loading);
                    throw e;
                });
        }
    };

    const handleUpdateUserDetails = (payload: TOnChangePayload, callBack?: () => void) => {
        void dispatchPromise(updateUserProfile(payload))
            .then(() => {
                const view = normalizeDashboardView(payload.dashboard_view ?? 'creator');
                /** `getHostDashboard` saga does not resolve `dispatchPromise` — fire-and-forget only */
                if (view === 'brand') {
                    dispatch(getHostDashboard({}));
                }
                const destination = getHomeRouteForView(view);
                callBack?.();
                /**
                 * Full reload guarantees we leave `/signup` and rehydrate app state with the new profile
                 * (avoids cases where in-place `navigate` did not switch the top-level route tree).
                 */
                window.location.replace(destination);
            })
            .catch(e => {
                SentryCapture(e);
                navigate(ROUTES.FEED, { replace: true });
            });
    };

    const handleLoadingState = () => setLoading(false);

    const userNameAvailable = (userName: string) => {
        if (userName.replaceAll(' ', '') !== '') {
            const data = { userName: userName } as SignUpdata;
            checkUserDataAvailability(data, handleLoadingState, 'userName');
        }
    };
    return (
        <View style={styles.container}>
            <EsSignupContainer
                getSignUpOTP={getSignUpOTP}
                userNameAvailable={userNameAvailable}
                onSubmitSignUpOTP={onSubmitSignUpOTP}
                checkUserDataAvailability={checkUserDataAvailability}
                otpLoading={otpLoading}
                loading={loading}
                resendInProgress={resendInProgress}
                updateUserDetails={handleUpdateUserDetails}
                initialFormData={initialFormData}
            />
        </View>
    );
};

export { Signup };
